/**
 * QuickPODModal — extracted from DriverView.js
 *
 * Mobile bottom-sheet that gives the driver three POD shortcuts on an active
 * STARTED dispatch:
 *   • Full POD Form — fires `onOpenFullPOD(dispatch)`
 *   • Quick Photo  — fires `onQuickPhoto()`
 *   • Mark Complete (Skip POD) — fires `onComplete(dispatch.id)`
 *
 * Pure controlled component: parent owns visibility (`open`) and the active
 * dispatch object, plus all three callbacks. Tapping the dark backdrop closes.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { ClipboardCheck, X, FileSignature, Camera, CheckCircle2, Loader2 } from 'lucide-react';

export const QuickPODModal = ({
  open,
  dispatch,
  onClose,
  onOpenFullPOD,
  onQuickPhoto,
  onComplete,
  actionLoading,
}) => {
  if (!open || !dispatch) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50"
      onClick={onClose}
      data-testid="quick-pod-modal"
    >
      <div
        className="bg-slate-900 border-t sm:border border-slate-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-green-400" />
              Quick POD Capture
            </h3>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="quick-pod-close-btn">
              <X className="h-5 w-5 text-slate-400" />
            </Button>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Dispatch Info */}
          <div className="bg-slate-800 rounded-lg p-3">
            <p className="text-white font-mono font-bold">{dispatch.container_number || 'N/A'}</p>
            <p className="text-slate-400 text-sm">{dispatch.destination || 'No destination'}</p>
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              className="h-20 flex-col gap-2 bg-purple-600 hover:bg-purple-500"
              onClick={() => { onClose(); onOpenFullPOD(dispatch); }}
              data-testid="quick-pod-full-btn"
            >
              <FileSignature className="h-6 w-6" />
              <span>Full POD Form</span>
            </Button>
            <Button
              className="h-20 flex-col gap-2 bg-blue-600 hover:bg-blue-500"
              onClick={() => { onClose(); onQuickPhoto(); }}
              data-testid="quick-pod-photo-btn"
            >
              <Camera className="h-6 w-6" />
              <span>Quick Photo</span>
            </Button>
          </div>

          <Button
            className="w-full bg-green-600 hover:bg-green-500 h-12"
            onClick={() => { onComplete(dispatch.id); onClose(); }}
            disabled={actionLoading === dispatch.id}
            data-testid="quick-pod-complete-btn"
          >
            {actionLoading === dispatch.id ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Mark Complete (Skip POD)
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
